import {
  RoutePersonalTransportUnavailableReasonEnum,
  RoutePlanCandidateRejectionReasonEnum,
  RoutePlanManualReasonEnum,
  RoutePlanUnplannableReasonEnum,
  RoutePlanWarningEnum,
} from 'api/model';

export type WarningSeverity = 'blocking' | 'decision' | 'info';

export interface WarningMeta {
  label: string;
  severity: WarningSeverity;
  icon?: 'bed' | 'clock';
}

/**
 * Stabila varningskoder från backend → svensk etikett + allvarlighetsgrad.
 * Rött (blocking) = kräver åtgärd innan verkställ, amber (decision) = kräver beslut
 * men går att verkställa, grått (info) = information. Se designmocken.
 */
export const getWarningMeta = (warning: RoutePlanWarningEnum): WarningMeta => {
  switch (warning) {
    case RoutePlanWarningEnum.HotelNeededButCannotStayAtHotels:
      return {
        label: 'FT kan ej bo på hotell',
        severity: 'blocking',
        icon: 'bed',
      };
    case RoutePlanWarningEnum.DrivingPolicyBlocked:
      return {
        label: 'Körtidsregler (EU-561) blockerar',
        severity: 'blocking',
      };
    case RoutePlanWarningEnum.HotelNeeded:
      return {
        label: 'Hotell behöver bokas',
        severity: 'decision',
        icon: 'bed',
      };
    case RoutePlanWarningEnum.BreakTimeInsufficient:
      return { label: 'Otillräcklig rasttid', severity: 'decision' };
    // Info: paketet har rast nog, men inte hela dagen med förarens befintliga uppdrag. Rasten som
    // saknas läggs av rastplaneraren när paketet verkställs.
    case RoutePlanWarningEnum.DayBreakTimeInsufficient:
      return { label: 'Dagen saknar rast', severity: 'info', icon: 'clock' };
    case RoutePlanWarningEnum.EndsAwayFromHome:
      return { label: 'Slutar utanför hemområdet', severity: 'decision' };
    case RoutePlanWarningEnum.ReturnByPersonalTransport:
      return { label: 'Hem med kollektivtrafik', severity: 'info' };
    // Info, inte beslut: benet ligger inom hemankarets län, så rutten är klar — men en lång
    // resa på egen hand är värd att se.
    case RoutePlanWarningEnum.LongCommute:
      return { label: 'Lång pendling', severity: 'info' };
    case RoutePlanWarningEnum.StartsAwayFromHome:
      return {
        label: 'Startar från övernattning',
        severity: 'info',
        icon: 'bed',
      };
    case RoutePlanWarningEnum.ContainsOnHold:
      return { label: 'Innehåller vilande uppdrag', severity: 'info' };
    case RoutePlanWarningEnum.EstimatesOnly:
      return { label: 'Endast uppskattade tider', severity: 'info' };
    // Info: paketet går att verkställa, men en heltidares timmar betalas ändå — TL ser att
    // dagen kan fyllas på för hand.
    case RoutePlanWarningEnum.ShortFullTimeDay:
      return { label: 'Kort dag för heltidare', severity: 'info' };
    // Info: prislistan saknar rad för sträckan, så intäkten är lägsta pris på samma sträcka.
    // Gäller bara planeringens TB — fakturaunderlaget visar fortfarande luckan.
    case RoutePlanWarningEnum.RevenueFromLowestRoutePrice:
      return { label: 'Schablonpris (lägsta på sträckan)', severity: 'info' };
    // Beslut: fälttestaren är behörig bara i nödfall för något i paketet.
    case RoutePlanWarningEnum.EmergencyCapability:
      return { label: 'Behörig bara i nödfall', severity: 'decision' };
    // Beslut: dagen sticker ut upp till toleransen utanför schemat — stäm av med föraren.
    case RoutePlanWarningEnum.OutsideScheduleWithinTolerance:
      return {
        label: 'Något utanför schemat',
        severity: 'decision',
        icon: 'clock',
      };
    // Beslut: elbil med dålig räckvidd (Fordonsklassificering). Laddstopp äter tid som planen
    // inte räknar med — överväg lastbil. Paketet förväljs ändå.
    case RoutePlanWarningEnum.LowRangeElectricTruckRecommended:
      return {
        label: 'Dålig räckvidd – rekommenderas lastbil',
        severity: 'decision',
      };
    // Info: ett ensamt uppdrag i förarens hemregion utan något att matcha med. TL-feedback
    // 2026-10-13 (#40): "vi behåller hellre 1 inköp här och där för framtida matchningar".
    case RoutePlanWarningEnum.LoneLegCouldWait:
      return { label: 'Kan vänta – ensamt uppdrag i hemlänet', severity: 'info' };
    // Blockerande: föraren hinner inte till sitt befintliga uppdrag efter paketet.
    case RoutePlanWarningEnum.MissesExistingAssignment:
      return {
        label: 'Hinner inte till nästa uppdrag',
        severity: 'blocking',
        icon: 'clock',
      };
    default:
      return { label: `Varning (${warning})`, severity: 'info' };
  }
};

export const getUnplannableReasonLabel = (
  reason: RoutePlanUnplannableReasonEnum
): string => {
  switch (reason) {
    case RoutePlanUnplannableReasonEnum.MissingCoordinates:
      return 'Saknar koordinater';
    case RoutePlanUnplannableReasonEnum.NoTimeWindowFit:
      return 'Ingen tidslucka passar';
    case RoutePlanUnplannableReasonEnum.ReservedForManualPlanning:
      return 'Reserverat för manuell planering';
    case RoutePlanUnplannableReasonEnum.ExcludedCaseType:
      return 'Planeras inte av ruttplaneringen';
    case RoutePlanUnplannableReasonEnum.PredecessorLegUnplanned:
      return 'Förtransporten är inte planerad';
    case RoutePlanUnplannableReasonEnum.FlexibleStock:
      return 'Lager att flytta';
    case RoutePlanUnplannableReasonEnum.ManualPlanningPlace:
      return 'Planeras alltid manuellt';
    default:
      return `Okänd anledning (${reason})`;
  }
};

/**
 * Varför en fälttestare aldrig blev kandidat. Skiljer sig från varningarna ovan: en
 * varning gäller en kandidat som finns, en avvisning förklarar en som saknas — och det
 * är den frågan planeraren ställer när listan är kortare än väntat.
 */
export const getCandidateRejectionLabel = (
  reason: RoutePlanCandidateRejectionReasonEnum
): string => {
  switch (reason) {
    case RoutePlanCandidateRejectionReasonEnum.AlreadyUsedInRun:
      return 'redan bokade på ett annat paket i förslaget';
    case RoutePlanCandidateRejectionReasonEnum.MissingCapability:
      return 'saknar behörighet för uppdraget';
    case RoutePlanCandidateRejectionReasonEnum.CommuteTooLong:
      return 'för lång pendling från hemankaret';
    case RoutePlanCandidateRejectionReasonEnum.NotAvailable:
      return 'otillgängliga den dagen';
    case RoutePlanCandidateRejectionReasonEnum.DrivingPolicyBlocked:
      return 'blockerade av körtidsreglerna (EU-561)';
    case RoutePlanCandidateRejectionReasonEnum.RunnerCustomerContact:
      return 'runners – paketet har kundkontakt (inköp, inbyte, auktion eller hemleverans)';
    case RoutePlanCandidateRejectionReasonEnum.CannotReachExistingAssignment:
      return 'hinner inte till sitt nästa uppdrag samma dag';
    default:
      return `okänd anledning (${reason})`;
  }
};

/**
 * Varför ett paket hamnade i den manuella listan. Orsaken är typad från backend just för att
 * åtgärden skiljer sig: en saknad hemresa löses av en strategi, en adress utan koordinat av att
 * någon rättar ärendet. En etikett som slog ihop dem hade dolt den skillnaden.
 */
export const getManualReasonLabel = (
  reason: RoutePlanManualReasonEnum
): string => {
  switch (reason) {
    case RoutePlanManualReasonEnum.NoReturnTransit:
      return 'Ingen hemresa';
    case RoutePlanManualReasonEnum.SpanExceedsLimit:
      return 'Dagen blir för lång';
    case RoutePlanManualReasonEnum.HubBacklogGrowing:
      return 'Hubben är full';
    case RoutePlanManualReasonEnum.MissingCoordinates:
      return 'Adress saknar koordinat';
    case RoutePlanManualReasonEnum.ReservedByPlanner:
      return 'Reserverat av planeraren';
    case RoutePlanManualReasonEnum.DrivingPolicyBlocked:
      return 'Körtidsregler (EU-561) blockerar';
    case RoutePlanManualReasonEnum.NoCandidate:
      return 'Ingen fälttestare kan ta det';
    case RoutePlanManualReasonEnum.HotelNotPossible:
      return 'Hotell krävs men går inte';
    case RoutePlanManualReasonEnum.ReturnLegNeedsCustomerRebooking:
      return 'Hemresa kräver ombokning med kund';
    case RoutePlanManualReasonEnum.OutboundTransitUnreasonable:
      return 'Orimlig utresa till första upphämtningen';
    case RoutePlanManualReasonEnum.OutboundHubStopoverNeedsReview:
      return 'Mellanlandning på utresa — kräver godkännande';
    case RoutePlanManualReasonEnum.PredecessorLegNotPlanned:
      return 'Förtransporten planeras inte i tid';
    case RoutePlanManualReasonEnum.AcquisitionNotCompleted:
      return 'Bilen köps samma dag av någon annan';
    case RoutePlanManualReasonEnum.PostalBoxAddress:
      return 'Postboxadress — riktig adress saknas';
    case RoutePlanManualReasonEnum.EmergencyCapability:
      return 'Bara behörig fälttestare i nödfall';
    case RoutePlanManualReasonEnum.RequiresCustomerRebooking:
      return 'Ny tid med kunden krävs';
    case RoutePlanManualReasonEnum.NightDriving:
      return 'Körning 00–05 — boka om eller dela sträckan';
    case RoutePlanManualReasonEnum.TransferTransitTooLate:
      return 'Överföringen hinns inte kollektivt';
    case RoutePlanManualReasonEnum.MissesExistingAssignment:
      return 'Hinner inte till sitt nästa uppdrag';
    case RoutePlanManualReasonEnum.MultiCarPurchase:
      return 'Flerbilsinköp — flera bilar hämtas samtidigt';
    default:
      return `Okänd orsak (${reason})`;
  }
};

/**
 * Allvarlighetsgrad på den manuella orsaken. Röd = dödläge som kräver ett annat upplägg, amber =
 * går att lösa i dagens plan med ett beslut, grå = någon annan än planeraren äger nästa steg.
 */
export const getManualReasonSeverity = (
  reason: RoutePlanManualReasonEnum
): WarningSeverity => {
  switch (reason) {
    // OutboundTransitUnreasonable är spegelbilden av NoReturnTransit och lika blockerande:
    // orsaken sätts bara när persontransporten är ENDA åtgärden på luckan, så det finns inget
    // val att fatta — bara en dag som inte går att verkställa som den står. Likaså NightDriving
    // (TL-feedback 2026-10-13, #0): ingen körning 00–05, och TransferTransitTooLate (ruttprovet
    // 2026-10-21, #42): överföringen hinns inte och har varken annons eller bil åt rätt håll.
    // MissesExistingAssignment är samma sak på luckan till förarens befintliga uppdrag.
    case RoutePlanManualReasonEnum.NoReturnTransit:
    case RoutePlanManualReasonEnum.OutboundTransitUnreasonable:
    case RoutePlanManualReasonEnum.NoCandidate:
    case RoutePlanManualReasonEnum.HotelNotPossible:
    case RoutePlanManualReasonEnum.DrivingPolicyBlocked:
    case RoutePlanManualReasonEnum.NightDriving:
    case RoutePlanManualReasonEnum.TransferTransitTooLate:
    case RoutePlanManualReasonEnum.MissesExistingAssignment:
      return 'blocking';
    // Benet före i samma ärende ligger i ett paket som inte tillämpas automatiskt. Löses genom
    // att förtransportens paket planeras — ett beslut i dagens plan, inget dödläge.
    case RoutePlanManualReasonEnum.PredecessorLegNotPlanned:
      return 'decision';
    // Bilen är inte köpt och någon annan köper den. Paketet går att verkställa — risken är att
    // inköpet faller och den som hämtar har åkt i onödan — men trafikledaren ska ha tagit ställning.
    case RoutePlanManualReasonEnum.AcquisitionNotCompleted:
      return 'decision';
    // Flerbilsinköp (beslut 2026-10-29): bilarna ska hämtas tillsammans, och automatiken planerar
    // dem inte som grupp än. Trafikledaren sätter ihop gänget — syskonens regnr står i spåret.
    case RoutePlanManualReasonEnum.MultiCarPurchase:
      return 'decision';
    // Postboxadressen geokodas till mitten av postnumret. Planeraren tar fram den riktiga
    // adressen med ett samtal till kunden eller bokaren — ett steg i dagens plan, inget dödläge.
    case RoutePlanManualReasonEnum.PostalBoxAddress:
      return 'decision';
    // Kandidaten finns, men är behörig bara i nödfall — trafikledaren avgör om det är ett nödfall.
    case RoutePlanManualReasonEnum.EmergencyCapability:
      return 'decision';
    // OutboundHubStopoverNeedsReview hör hit och inte till de blockerande: mellanlandningen är
    // föreslagen och färdigräknad, det som saknas är trafikledarens ja. Likaså
    // RequiresCustomerRebooking: paketet är räknat på en ny tid, det som saknas är kundens ja.
    case RoutePlanManualReasonEnum.SpanExceedsLimit:
    case RoutePlanManualReasonEnum.HubBacklogGrowing:
    case RoutePlanManualReasonEnum.ReturnLegNeedsCustomerRebooking:
    case RoutePlanManualReasonEnum.OutboundHubStopoverNeedsReview:
    case RoutePlanManualReasonEnum.RequiresCustomerRebooking:
      return 'decision';
    default:
      return 'info';
  }
};

/**
 * Varför kollektivtrafiken inte gav något förslag. Samma princip som annonsraden fick 2026-08-25:
 * ett uteblivet förslag ska säga varför, annars går "det finns ingen buss" inte att skilja från
 * "vi hann inte fråga".
 */
export const getPersonalTransportUnavailableLabel = (
  reason: RoutePersonalTransportUnavailableReasonEnum
): string => {
  switch (reason) {
    case RoutePersonalTransportUnavailableReasonEnum.NoTransitFound:
      return 'ingen förbindelse hittades';
    case RoutePersonalTransportUnavailableReasonEnum.BudgetExhausted:
      return 'dagens sökbudget var slut';
    case RoutePersonalTransportUnavailableReasonEnum.LookupFailed:
      return 'sökningen misslyckades';
    default:
      return `okänd anledning (${reason})`;
  }
};
